import React from 'react';
import * as XLSX from 'xlsx-js-style';
import {
  exportWeeklyGridPDFBySpot, exportWeeklyGridPDFByEmployee, exportSingleEmployeePDF,
  exportSingleSpotPDF, exportIndividualSpotPDFsZip } from 'util/exportPDF';
import WeekPicker from 'ui/components/WeekPicker';
import { Shift } from 'domain/Shift';
import { Spot } from 'domain/Spot';
import { Employee } from 'domain/Employee';
import { AppState } from 'store/types';
import { getMaxDateTime } from 'store/shift/selectors';
import { calculateAvailabilityCard } from './AvailabilityCardHelper';
import { EmployeeAvailability } from 'domain/EmployeeAvailability';
import { rosterOperations, rosterSelectors } from 'store/roster';
import { spotSelectors } from 'store/spot';
import { employeeSelectors } from 'store/employee';
import { connect } from 'react-redux';
import { alert } from 'store/alert';
import moment from 'moment';
import {
  Button, EmptyState, EmptyStateVariant, Title, EmptyStateBody, EmptyStateIcon, Tabs,
  Tab, Form, Flex
} from '@patternfly/react-core';
import { RosterState } from 'domain/RosterState';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { CubesIcon, FileExcelIcon, FilePdfIcon } from '@patternfly/react-icons';
import { WithTranslation, Trans, withTranslation } from 'react-i18next';
import { getPropsFromUrl, UrlProps } from 'util/BookmarkableUtils';
import { IndictmentSummary } from 'domain/indictment/IndictmentSummary';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import { shiftOperations } from 'store/shift';
import { holidayOperations, holidaySelectors } from 'store/holiday';
import { Holiday } from 'domain/Holiday';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { CSSProperties } from 'react';
import { refreshHolidayList } from 'store/holiday/operations';

/* eslint-disable */

interface StateProps {
  tenantId: number;
  isSolving: boolean;
  isLoading: boolean;
  holidayList: Holiday[];
  allSpotList: Spot[];
  employeeList: Employee[];
  shownSpotList: Spot[];
  maxShiftEndDate: Date | null;
  spotIdToShiftListMap: Map<number, Shift[]>;
  employeeIdToAvailabilityListMap: Map<number, EmployeeAvailability[]>;
  rosterState: RosterState | null;
  indictmentSummary: IndictmentSummary | null;
}

export type ShiftRosterUrlProps = UrlProps<'spot'|'week'>;
// Snapshot of the last value to show when loading
let lastSpotIdToShiftListMap: Map<number, Shift[]> = new Map<number, Shift[]>();
let lastShownSpotList: Spot[] = [];
let lastEmployeeIdToAvailabilityListMap:
Map<number, EmployeeAvailability[]> = new Map<number, EmployeeAvailability[]>();

// eslint-disable-next-line no-return-assign
const mapStateToProps = (state: AppState): StateProps => {
  return {
    tenantId: state.tenantData.currentTenantId,
    isSolving: state.solverState.solverStatus !== 'NOT_SOLVING',
    isLoading: rosterSelectors.isShiftRosterLoading(state),
    holidayList: holidaySelectors.getHolidayList(state),
    allSpotList: spotSelectors.getSpotList(state),
    employeeList: employeeSelectors.getActiveEmployeeList(state),
    // The use of "x = isLoading? x : getUpdatedData()" is a way to use old value if data is still loading
    shownSpotList: lastShownSpotList = rosterSelectors.isShiftRosterLoading(state)
      ? lastShownSpotList
      : rosterSelectors.getSpotListInShiftRoster(state)
        .filter(spot => {
          if( spot.status ) return true;

          return rosterSelectors.getShiftListForSpot(state,spot).length > 0;
        }),
    maxShiftEndDate: getMaxDateTime(state),
    spotIdToShiftListMap: lastSpotIdToShiftListMap = rosterSelectors.getSpotListInShiftRoster(state)
      .filter(spot => {
        if (spot.status) return true;

        return rosterSelectors.getShiftListForSpot(state, spot).length > 0;
      })
      .reduce(
        (prev, curr) => prev.set(
          curr.id as number,
          rosterSelectors.getShiftListForSpot(state, curr),
        ),
        // reducing an empty array returns the starting value
        rosterSelectors.isShiftRosterLoading(state) ? lastSpotIdToShiftListMap : new Map<number, Shift[]>(),
      ),
    employeeIdToAvailabilityListMap: lastEmployeeIdToAvailabilityListMap = rosterSelectors
      .getEmployeeListInAvailabilityRoster(state)
      .reduce(
        (prev, curr) => prev.set(
          curr.id as number,
          rosterSelectors.getAvailabilityListForEmployee(state, curr),
        ),
        rosterSelectors.isAvailabilityRosterLoading(state) ? lastEmployeeIdToAvailabilityListMap
          : new Map<number, EmployeeAvailability[]>(),
      ),
    rosterState: state.rosterState.rosterState,
    indictmentSummary: state.shiftRoster.shiftRosterView ? state.shiftRoster.shiftRosterView.indictmentSummary : null,
  };
};


export interface DispatchProps {
  getShiftRosterFor: typeof rosterOperations.getShiftRosterFor;
  showInfoMessage: typeof alert.showInfoMessage;
  loadMaxShiftEndDate: typeof shiftOperations.loadMaxShiftEndDate;
  refreshHolidayList: typeof holidayOperations.refreshHolidayList;
  getAvailabilityRosterFor: typeof rosterOperations.getAvailabilityRosterFor;
}

const mapDispatchToProps = (dispatch: ThunkDispatch<AppState, any, AnyAction>) => ({
  getShiftRosterFor: (args: Parameters<typeof rosterOperations.getShiftRosterFor>[0]) =>
    dispatch(rosterOperations.getShiftRosterFor(args)),

  showInfoMessage: (msg: string) =>
    dispatch(alert.showInfoMessage(msg)),

  loadMaxShiftEndDate: (tenantId: number) =>
    dispatch(shiftOperations.loadMaxShiftEndDate(tenantId)),

  refreshHolidayList: () =>
    dispatch(holidayOperations.refreshHolidayList()),

  getAvailabilityRosterFor: (
    args: Parameters<typeof rosterOperations.getAvailabilityRosterFor>[0]
  ) =>
    dispatch(rosterOperations.getAvailabilityRosterFor(args)),
});

export type Props = RouteComponentProps & StateProps & DispatchProps & WithTranslation;
interface State {
  isCreatingOrEditingShift: boolean;
  isExportingSchedule: boolean;
  isProvisioningShifts: boolean;
  selectedShift?: Shift;
  firstLoad: boolean;
  isLoading: boolean;
  activeTabKey: number;
  fromDate: string;
  toDate: string;
  weekStart: Date;
}

export class ShiftSchedulePage extends React.Component<Props, State> {

  timelineRef: React.RefObject<HTMLDivElement> = React.createRef();
  employeeTimelineRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: Props) {
    super(props);
    this.onUpdateShiftRoster = this.onUpdateShiftRoster.bind(this);
    this.state = {
      isCreatingOrEditingShift: false,
      isExportingSchedule: false,
      isProvisioningShifts: false,
      firstLoad: true,
      isLoading: false,
      activeTabKey: 0,
      fromDate: moment().startOf('week').format('YYYY-MM-DD'),
      toDate: moment().endOf('week').format('YYYY-MM-DD'),
      weekStart: moment().startOf('week').toDate()
    };
  }

  onUpdateShiftRoster(urlProps: ShiftRosterUrlProps) {
    if (this.props.rosterState && this.props.maxShiftEndDate && this.props.allSpotList.length > 0) {
      const startDate = moment().startOf('week').toDate();
      const endDate = moment(this.props.maxShiftEndDate).isValid()
        ? new Date(this.props.maxShiftEndDate)
        : moment().endOf('week').toDate();
      
      this.props.getShiftRosterFor({
        fromDate: startDate,
        toDate: endDate,
        spotList: this.props.allSpotList,
      });

      this.props.getAvailabilityRosterFor({
        fromDate: startDate,
        toDate: endDate,
        employeeList: this.props.employeeList,
      });

      if (this.state.firstLoad) {
        this.setState({ firstLoad: false });
      }
    }
  }

  private isHolidayOrSunday(day: moment.Moment): boolean {
    const date = day.format('YYYY-MM-DD');
    const isSunday = day.day() === 0;
    
    const isHoliday = this.props.holidayList.some(
      holiday => moment(holiday.date).format('YYYY-MM-DD') === date
    );

    return isSunday || isHoliday;
  }

  private renderWeeklyGridBySpot = () => {
    const thStyle: CSSProperties = {
      border: '1px solid #ccc',
      padding: '8px',
      background: '#f5f5f5',
      textAlign: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 2
    };

    const tdStyle: CSSProperties = {
      border: '1px solid #ccc',
      padding: '6px',
      verticalAlign: 'top'
    };

    const cardStyle: CSSProperties = {
      background: '#4084c7',
      color: 'white',
      padding: '6px',
      marginBottom: '4px',
      borderRadius: '6px',
      fontSize: '15px'
    };

    const timeStyle: CSSProperties = {
      fontSize: '15px',
      fontWeight: 'bold'
    };

    const extraStyle: CSSProperties = {
      fontSize: '15px',
      opacity: 0.8
    };

    const firstColumnStyle: React.CSSProperties = {
      border: '1px solid #ccc',
      padding: '6px',
      background: '#fff',
      position: 'sticky',
      left: 0,
      zIndex: 1
    };

    const { shownSpotList, spotIdToShiftListMap } = this.props;

    const startOfWeek = moment(this.state.weekStart).startOf('week');
    const days = Array.from({ length: 7 }).map((_, i) =>
      startOfWeek.clone().add(i, 'days')
    );

    return (
      <div style={{ height: 'calc(100vh - 260px)', overflow: 'auto'}}>
        <table style={{ width: '100%', borderCollapse: 'collapse', height: '1px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Cliente</th>
              
              {days.map((day) => {
                const specialDay = this.isHolidayOrSunday(day);
                return(
                  <th
                    key={day.format()}
                    style={{
                      ...thStyle,
                      background: specialDay ? '#ffe1e1' : thStyle.background
                    }}      
                  >
                    {day.format('ddd DD/MM')}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {shownSpotList.map((spot) => {
              const shifts = spotIdToShiftListMap?.get(spot.id!) || [];

              return (
                <tr key={spot.id}>
                  <td style={{...tdStyle, ...firstColumnStyle}}>
                    <b>{spot.name}</b><br />
                    <small>{spot.isApartment ? 'Apartamento' : 'General'}</small>
                    <div>
                      <Button
                        variant='secondary'
                        style={{
                          fontSize: '12px',
                          padding: '4px 6px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          const shifts = spotIdToShiftListMap.get(spot.id!) || [];
                          exportSingleSpotPDF(spot, shifts, this.state.weekStart, this.props.holidayList.map(h => h.date));
                        }}
                      >
                        <FilePdfIcon />
                      </Button>
                    </div>
                  </td>

                  {days.map((day) => {
                    const shiftsForDay = shifts
                      .filter(s =>
                        moment(s.startDateTime).isSame(day, 'day')
                      )
                      .sort((a, b) =>
                        moment(a.startDateTime).diff(moment(b.startDateTime))
                      );

                    return (
                      <td
                        key={day.format()}
                        style={{
                          ...tdStyle,
                          background: this.isHolidayOrSunday(day) ? '#fff5f5' : undefined
                        }}
                      >
                        {shiftsForDay.map(shift => (
                          <div key={shift.id} style={cardStyle}>
                            <b>{shift.employee?.name || 'Sin asignar'}</b>
                            <div style={timeStyle}>
                              {moment(shift.startDateTime).format('hh:mm A')} - {moment(shift.endDateTime).format('hh:mm A')}
                            </div>

                            {/* 🔥 INFO EXTRA */}
                            {shift.rotationEmployee && (
                              <div style={extraStyle}>
                                🔁 {shift.rotationEmployee.name}
                              </div>
                            )}
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ height: 'calc(100vh - 340px)' }} />
      </div>
    );
  }

  private renderWeeklyGridByEmployee = () => {

    const thStyle: React.CSSProperties = {
      border: '1px solid #ccc',
      padding: '8px',
      background: '#f5f5f5',
      textAlign: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 2
    };

    const tdStyle: React.CSSProperties = {
      border: '1px solid #ccc',
      padding: '6px',
      verticalAlign: 'top'
    };

    const cardStyle: React.CSSProperties = {
      padding: '8px',
      marginBottom: '6px',
      borderRadius: '8px',
      fontSize: '13px',
      border: '1px solid transparent',
      boxSizing: 'border-box',
      width: '100%',
    };

    const greenCardStyle: React.CSSProperties = {
      background: '#2bdc5d',
      color: 'white',
      padding: '6px',
      marginBottom: '4px',
      borderRadius: '6px',
      fontSize: '15px'
    };

    const timeStyle: React.CSSProperties = {
      fontSize: '13px',
      fontWeight: 'bold',
      marginBottom: '5px',
    };

    const extraStyle: React.CSSProperties = {
      fontSize: '12px',
      opacity: 0.85,
      marginTop: '3px',
      overflowWrap: 'anywhere',
    };

    const firstColumnStyle: React.CSSProperties = {
      border: '1px solid #ccc',
      padding: '6px',
      background: '#fff',
      position: 'sticky',
      left: 0,
      zIndex: 1
    };

    const redCardStyle: React.CSSProperties = {
      background: '#c9190b',
      color: 'white',
      padding: '6px',
      marginBottom: '4px',
      borderRadius: '6px',
      fontSize: '15px'
    };

    type ShiftType = 'morning' | 'afternoon' | 'full';

    const shiftColors: Record<
      ShiftType,
      {
        background: string;
        border: string;
        text: string;
        label: string;
      }
    > = {
      morning: {
        background: '#E0F2FE',
        border: '#7DD3FC',
        text: '#075985',
        label: 'MAÑANA',
      },
      afternoon: {
        background: '#FFF0D6',
        border: '#FDBA74',
        text: '#92400E',
        label: 'TARDE',
      },
      full: {
        background: '#EDE9FE',
        border: '#C4B5FD',
        text: '#5B21B6',
        label: 'JORNADA COMPLETA',
      },
    };

    const getShiftType = (shift: Shift): ShiftType => {
      const start = moment(shift.startDateTime);
      const end = moment(shift.endDateTime);
      const noon = start.clone().startOf('day').hour(12);

      if (start.isBefore(noon) && end.isAfter(noon)) {
        return 'full';
      }

      if (start.isBefore(noon)) {
        return 'morning';
      }

      return 'afternoon';
    };

    const { allSpotList, spotIdToShiftListMap } = this.props;

    // 🔥 IMPORTANTE: usar semana seleccionada
    const startOfWeek = moment(this.state.weekStart).startOf('week');

    const days = Array.from({ length: 7 }).map((_, i) =>
      startOfWeek.clone().add(i, 'days')
    );

    // 🔥 MAP DE EMPLEADOS
    const employeeMap: Map<number, {  employee: Employee | null; name: string; shifts: Shift[]; }> = new Map();

    allSpotList.forEach(spot => {
      const shifts = spotIdToShiftListMap?.get(spot.id!) || [];

      shifts.forEach(shift => {

        // 🔥 CASO: turno sin empleado → agrupar como "Sin asignar"
        const empId = shift.employee?.id ?? -1;
        const empName = shift.employee?.name ?? 'Sin asignar';

        if (!employeeMap.has(empId)) {
          employeeMap.set(empId, {
            employee: shift.employee ?? null,
            name: empName,
            shifts: []
          });
        }

        employeeMap.get(empId)!.shifts.push(shift);
      });
    });

    // 🔥 SI NO HAY DATOS
    if (employeeMap.size === 0) {
      return <div style={{ padding: '1rem' }}>No hay turnos en esta semana</div>;
    }

    return (
      <div style={{ height: 'calc(100vh - 260px)', overflow: 'auto'}}>
        <table style={{ width: '100%', borderCollapse: 'collapse', height: '1px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Empleado</th>
          
              {days.map((day) => {
                  const specialDay = this.isHolidayOrSunday(day);
                  return(
                    <th
                      key={day.format()}
                      style={{
                        ...thStyle,
                        background: specialDay ? '#ffe1e1' : thStyle.background
                      }}      
                    >
                      {day.format('ddd DD/MM')}
                    </th>
                  );
                })}

            </tr>
          </thead>

          <tbody>
            {Array.from(employeeMap.entries()).map(([empId, emp]) => (

              
              <tr key={empId}>
                <td style={{...tdStyle, ...firstColumnStyle}}>
                  <b>{emp.name}</b>

                  <div>
                    <Button
                      variant='secondary'
                      style={{
                        fontSize: '12px',
                        padding: '4px 6px',
                        cursor: 'pointer'
                      }}
                      onClick={() => exportSingleEmployeePDF(emp, this.state.weekStart, this.props.holidayList.map(h => h.date))}
                    >
                      <FilePdfIcon />
                    </Button>
                  </div>
                </td>

                {days.map((day) => {
                  const shiftsForDay = emp.shifts
                    .filter(s =>
                      moment(s.startDateTime).isSame(day, 'day')
                    )
                    .sort((a, b) =>
                      moment(a.startDateTime).diff(moment(b.startDateTime))
                    );

                  const {
                      showCardBefore,
                      showCardAfter,
                      showUnavailableCardBefore,
                      showUnavailableCardAfter,
                      unavailableReason,
                      unavailableStart,
                      unavailableEnd,
                      unavailableAllDay,
                  } = calculateAvailabilityCard(
                      emp.employee,
                      day,
                      emp.shifts,
                      this.props.holidayList,
                      this.props.employeeIdToAvailabilityListMap.get(empId) ?? []
                  );

                  return (
                  
                    <td
                      key={day.format()}
                      style={{
                        ...tdStyle,
                        background: this.isHolidayOrSunday(day) ? '#fff5f5' : undefined,
                        height: '100%',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          minHeight: '100%',
                        }}
                      >
                      {showCardBefore && (
                        <div style={greenCardStyle}>
                            Disponible para programar
                        </div>
                      )}

                      {showUnavailableCardBefore && (
                        <div style={redCardStyle}>
                          <b>No disponible</b>
                          {unavailableReason && (
                            <>
                              <br />
                              {this.props.t(`AvailabilityReason.${unavailableReason}`)}
                            </>
                          )}

                          {unavailableAllDay ? (
                            <>
                              <br />
                              <b>Todo el día</b>
                            </>
                          ) : (
                            unavailableStart &&
                            unavailableEnd && (
                              <>
                                <br />
                                <b>
                                  {unavailableStart} - {unavailableEnd}
                                </b>
                              </>
                            )
                          )}
                        </div>
                      )}

                      {shiftsForDay.map(shift => {
                          const type = getShiftType(shift);
                          const colors = shiftColors[type];

                          const isSingleFullDay =
                            shiftsForDay.length === 1 &&
                            type === 'full';

                          return (
                            <div
                              key={shift.id}
                              style={{
                                ...cardStyle,
                                background: colors.background,
                                borderColor: colors.border,
                                color: '#334155',

                                // Jornada completa: ocupa el espacio disponible
                                flexGrow: isSingleFullDay ? 1 : 0,
                                minHeight: isSingleFullDay ? '100%' : undefined,
                              }}
                            >
                              {/* IDENTIFICACIÓN DEL TURNO */}
                              <div
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  color: colors.text,
                                  marginBottom: '5px',
                                }}
                              >
                                {colors.label}
                              </div>

                              {/* HORARIO */}
                              <div
                                style={{
                                  ...timeStyle,
                                  color: colors.text,
                                }}
                              >
                                {moment(shift.startDateTime).format('h:mm A')}
                                {' - '}
                                {moment(shift.endDateTime).format('h:mm A')}
                              </div>

                              {/* NOMBRE DEL SPOT */}
                              <div
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '13px',
                                  marginBottom: '5px',
                                  overflowWrap: 'anywhere',
                                }}
                              >
                                {shift.spot.name}
                              </div>

                              {/* INFORMACIÓN DEL SPOT */}
                              {shift.spot.address && (
                                <div style={extraStyle}>
                                  {shift.spot.address}
                                </div>
                              )}

                              {shift.spot.city && (
                                <div style={extraStyle}>
                                  {shift.spot.city}
                                </div>
                              )}

                              {shift.spot.observations && (
                                <div style={extraStyle}>
                                  {shift.spot.observations}
                                </div>
                              )}
                            </div>
                          );
                        })}

                      {showCardAfter && (
                        <div style={greenCardStyle}>
                          Disponible para programar
                        </div>
                      )}

                      {showUnavailableCardAfter && (
                        <div style={redCardStyle}>
                          <b>No disponible</b>

                          {unavailableAllDay ? (
                            <>
                              <br />
                              <b>Todo el día</b>
                            </>
                          ) : (
                            unavailableStart &&
                            unavailableEnd && (
                              <>
                                <br />
                                <b>
                                  {unavailableStart} - {unavailableEnd}
                                </b>
                              </>
                            )
                          )}
                        </div>
                      )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ height: 'calc(100vh - 340px)' }} />
      </div>
    );
  };

  handleTabClick = (event: React.MouseEvent<HTMLElement>, tabIndex: number) => {
    this.setState({ activeTabKey: tabIndex }, () => {
      if (tabIndex === 0) this.renderWeeklyGridBySpot();
      else this.renderWeeklyGridByEmployee();
    });
  };

  handleFromDateChange = (_event: React.FormEvent<HTMLInputElement>, value: string, date?: Date) => {
    if (date) {
      this.setState({ fromDate: value });
    }
  };

  handleToDateChange = (_event: React.FormEvent<HTMLInputElement>, value: string, date?: Date) => {
    if (date) {
      this.setState({ toDate: value });
    }
  };

  componentDidMount() {
    this.props.loadMaxShiftEndDate(this.props.tenantId);
    this.props.refreshHolidayList();
  }

  componentDidUpdate(prevProps: Props) {
    const urlProps = getPropsFromUrl<ShiftRosterUrlProps>(this.props, {
      spot: null,
      week: null,
    });

    const dataReadyNow =
    this.props.rosterState !== null &&
    this.props.allSpotList.length > 0 &&
    this.props.maxShiftEndDate !== null &&
    this.state.firstLoad;

    if (dataReadyNow) {
      this.setState({ firstLoad: false });
      const toDate = moment(this.props.maxShiftEndDate).isValid()
        ? moment(this.props.maxShiftEndDate).format('YYYY-MM-DD')
        : moment().endOf('week').format('YYYY-MM-DD');

      this.setState({ toDate });
      this.onUpdateShiftRoster(urlProps);
      return;
    }

    if (
      prevProps.spotIdToShiftListMap !== this.props.spotIdToShiftListMap ||
      prevProps.shownSpotList !== this.props.shownSpotList
    ) {
      this.renderWeeklyGridBySpot();
    }
  }

  isDateRangeValid(): boolean {
    const { fromDate, toDate } = this.state;

    if (!fromDate || !toDate) return false;

    const startDate = moment(fromDate, 'YYYY-MM-DD').toDate();
    const endDate = moment(toDate, 'YYYY-MM-DD').toDate();

    return endDate >= startDate;
  }

  generateExcelBySpot = () => {
    const { allSpotList, spotIdToShiftListMap } = this.props;

    const start = moment(this.state.fromDate, 'YYYY-MM-DD');
    const end = moment(this.state.toDate, 'YYYY-MM-DD');
    const dayList: string[] = [];
    const dates: string[] = [];

    for (let m = moment(start); m.isSameOrBefore(end); m.add(1, 'days')) {
      dayList.push(m.format('ddd. DD/MM/YYYY'));
      dates.push(m.format('YYYY-MM-DD'));
    }

    const table: any[][] = [];
    table.push(['Puesto', ...dayList]);

    allSpotList.forEach(spot => {
      const shifts = spotIdToShiftListMap.get(spot.id!) || [];
      const row: string[] = [spot.name];
      const diaAsignado: Record<string, { nombre: string, horaInicio: string, horaFin: string }[]> = {};

      shifts.forEach(shift => {
        const fecha = moment(shift.startDateTime).format('YYYY-MM-DD');
        const nombre = shift.employee?.name || 'No asignado';
        const horaInicio = moment(shift.startDateTime).format('hh:mm A');
        const horaFin = moment(shift.endDateTime).format('hh:mm A');

        if (!diaAsignado[fecha]) diaAsignado[fecha] = [];
        diaAsignado[fecha].push({ nombre, horaInicio, horaFin });
      });

      dates.forEach(fecha => {
        if (!diaAsignado[fecha]) {
          row.push('');
        } else {
          const lineas = diaAsignado[fecha]
            .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio))
            .map(({ nombre, horaInicio, horaFin }) => `${nombre} (${horaInicio} - ${horaFin})`);
          row.push(lineas.join('\n'));
        }
      });

      table.push(row);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(table);

    // Aplicar estilo visual (bordes + negrita + wrap)
    const range = XLSX.utils.decode_range(worksheet['!ref']!);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cell_address];
        if (!cell) continue;
        if (!cell.s) cell.s = {};
        cell.s.alignment = { wrapText: true, vertical: 'top' };
        cell.s.border = {
          top:    { style: "thin", color: { auto: 1 } },
          right:  { style: "thin", color: { auto: 1 } },
          bottom: { style: "thin", color: { auto: 1 } },
          left:   { style: "thin", color: { auto: 1 } },
        };
        if (R === 0) cell.s.font = { bold: true };
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Turnos por Puesto');
    XLSX.writeFile(workbook, 'turnos_por_puesto.xlsx');
  };

  generateExcelByEmployee = () => {
    const { allSpotList, spotIdToShiftListMap } = this.props;

    const start = moment(this.state.fromDate, 'YYYY-MM-DD');
    const end = moment(this.state.toDate, 'YYYY-MM-DD');
    const dayList: string[] = [];
    const dates: string[] = [];

    for (let m = moment(start); m.isSameOrBefore(end); m.add(1, 'days')) {
      dayList.push(m.format('ddd. DD/MM/YYYY'));
      dates.push(m.format('YYYY-MM-DD'));
    }

    const table: any[][] = [];
    table.push(['Empleado', ...dayList]);

    const employeeMap: Map<number, { name: string, shifts: Shift[] }> = new Map();

    allSpotList.forEach(spot => {
      const shifts = spotIdToShiftListMap.get(spot.id!) || [];
      shifts.forEach(shift => {
        if (!shift.employee) return;
        const empId = shift.employee.id!;
        if (!employeeMap.has(empId)) {
          employeeMap.set(empId, { name: shift.employee.name, shifts: [] });
        }
        employeeMap.get(empId)!.shifts.push(shift);
      });
    });

    employeeMap.forEach(emp => {
      const row: string[] = [emp.name];
      const diaAsignado: Record<string, Record<string, string[]>> = {};

      emp.shifts.forEach(shift => {
        const fecha = moment(shift.startDateTime).format('YYYY-MM-DD');
        const puesto = shift.spot.name;
        const horaInicio = moment(shift.startDateTime).format('hh:mm A');
        const horaFin = moment(shift.endDateTime).format('hh:mm A');
        if (!diaAsignado[fecha]) diaAsignado[fecha] = {};
        if (!diaAsignado[fecha][puesto]) diaAsignado[fecha][puesto] = [];
        diaAsignado[fecha][puesto].push(`${horaInicio} - ${horaFin}`);
      });

      dates.forEach(fecha => {
        if (!diaAsignado[fecha]) {
          row.push('');
        } else {
          const lineas = Object.entries(diaAsignado[fecha])
            .map(([puesto, rangos]) => {
              // Extraer la hora de inicio del primer rango
              const primeraHora = rangos[0]?.split(' - ')[0] || '00:00';
              return { puesto, rangos, horaInicio: primeraHora };
            })
            .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio)) // ordenar por hora
            .map(({ puesto, rangos }) => {
              const contenido = rangos.length === 1
                ? `(${rangos[0]})`
                : `(${rangos.join(', ')})`;
              return `${puesto} ${contenido}`;
            });
          row.push(lineas.join('\n'));
        }
      });

      table.push(row);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(table);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Turnos por Empleado');
    XLSX.writeFile(workbook, 'turnos_por_empleado.xlsx');
  };

  render(){
    const { t } = this.props;
    const changedTenant = this.props.shownSpotList.length === 0
      || this.props.tenantId !== (this.props.shownSpotList[0]).tenantId;

    if (this.props.shownSpotList.length === 0 || this.state.firstLoad
        || changedTenant || this.props.rosterState === null) {
      return (
        <>
          { (this.props.isLoading || this.state.isLoading) && (!this.props.isSolving) ? <LoadingSpinner isLoading /> : '' }
          <EmptyState variant={EmptyStateVariant.full}>
            <EmptyStateIcon icon={CubesIcon} />
            <Trans
              t={t}
              i18nKey="noShiftsViewGeneral"
              components={[
                <Title headingLevel="h5" size="lg" key={0} />,
                <EmptyStateBody key={1} />,
                <Button
                  key={2}
                  aria-label="Wards Page"
                  variant="primary"
                  onClick={() => this.props.history.push(`/${this.props.tenantId}/wards`)}
                />,
              ]}
            />
          </EmptyState>
        </>
      );
    }

    return (
      <>
        { (this.props.isLoading || this.state.isLoading) && (!this.props.isSolving) ? <LoadingSpinner isLoading /> : '' }
        <div style={{ padding: '2rem', backgroundColor: 'white' }}>
          <div>
            <Form onSubmit={(e) => {
              e.preventDefault();
              const startDate = moment(this.state.fromDate, 'YYYY-MM-DD').toDate();
              const endDate = moment(this.state.toDate, 'YYYY-MM-DD').toDate();
              
              this.props.getShiftRosterFor({
                fromDate: startDate,
                toDate: endDate,
                spotList: this.props.allSpotList,
              });

              this.props.getAvailabilityRosterFor({
                fromDate: startDate,
                toDate: endDate,
                employeeList: this.props.employeeList,
              });
              
            }}>
              <Flex direction={{ default: 'row' }} spaceItems={{ default: 'spaceItemsMd' }}>
                <h1>Consultar semana: </h1>
                <div style={{ position: 'relative', zIndex: 100 }}>
                  <WeekPicker
                    aria-label="Select Week to View"
                    value={this.state.weekStart}
                    // minDate={this.props.rosterState ? this.props.rosterState.firstDraftDate : undefined}
                    onChange={(weekStart) => {
                      const fromDate = moment(weekStart).startOf('week').toDate();
                      const toDate = moment(weekStart).endOf('week').toDate();

                      this.setState({
                        weekStart,
                        fromDate: moment(fromDate).format('YYYY-MM-DD'),
                        toDate: moment(toDate).format('YYYY-MM-DD'),
                      });

                      this.props.getShiftRosterFor({
                        fromDate,
                        toDate,
                        spotList: this.props.allSpotList,
                      });

                      this.props.getAvailabilityRosterFor({
                        fromDate,
                        toDate,
                        employeeList: this.props.employeeList,
                      });
                    }}
                  />
                </div>
                
              </Flex>
            </Form>
          </div>
          <div style={{paddingTop: '2rem'}}>
            <Tabs activeKey={this.state.activeTabKey} onSelect={this.handleTabClick}>
              <Tab eventKey={0} title={t('bySpot')}>
                <div
                  style={{ marginTop: '0.5rem' }}
                >
                  {/* <Button variant="primary" style={{ marginBottom: '1rem' }} alt='Exportar a excel' onClick={this.generateExcelBySpot}>
                    <FileExcelIcon />
                  </Button> */}
                  <Button
                    style={{ marginLeft: '1rem'}}
                    onClick={async () => {
                      try {
                        this.setState({ isLoading: true });
                          exportIndividualSpotPDFsZip(
                            this.props.shownSpotList,
                            this.props.spotIdToShiftListMap,
                            this.state.weekStart,
                            this.props.holidayList.map(h => h.date)
                          );
                        } finally {
                          this.setState({ isLoading: false });
                        }
                      }
                    }
                  >
                    <FilePdfIcon />
                </Button>
                </div>
                <div style={{ marginTop: '0.2rem' }}>
                  {this.renderWeeklyGridBySpot()}
                </div>
              </Tab>
              <Tab eventKey={1} title={t('byEmployee')}>
                <div
                  style={{ marginTop: '0.5rem' }}
                >
                  {/* <Button variant="primary" style={{ marginBottom: '1rem' }} alt='Exportar a excel' onClick={this.generateExcelByEmployee}>
                    <FileExcelIcon />
                  </Button> */}
                  {/* <Button
                    style={{ marginLeft: '1rem'}}
                    onClick={async () => {
                      try {
                        this.setState({ isLoading: true });

                        exportWeeklyGridPDFByEmployee(
                          this.props.shownSpotList,
                          this.props.spotIdToShiftListMap,
                          this.state.weekStart,
                          this.props.holidayList.map(h => h.date)
                        );

                      } finally {
                        this.setState({ isLoading: false });
                      }
                    }}
                  >
                    <FilePdfIcon />
                  </Button> */}
                </div>
                <div style={{ marginTop: '0.2rem' }}>
                  {this.renderWeeklyGridByEmployee()}
                </div>
              </Tab>
            </Tabs>
          </div>
        </div>
      </>
    );
  }
};

export default withTranslation('ShiftSchedulePage')(connect(mapStateToProps, mapDispatchToProps)(withRouter(ShiftSchedulePage)));
